/**
 * Tasks — combined page.
 * - Every user sees "Assigned to me" (inbox) section.
 * - Users with can_assign_tasks ALSO see "Assigned by me" section
 *   with Pending/Completed stat cards + the big blue + card.
 * - Rows are clickable → opens TaskDetailModal (description, file, status, re-notify).
 * - Status pill on rows is read-only here; the modal is where edits happen.
 */
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Bell, Download } from 'lucide-react';
import { useNotifications } from '@/contexts/NotificationContext';
import { useAuth } from '@/contexts/AuthContext';
import TaskDetailModal from '@/components/TaskDetailModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const StatusPill = ({ status }) => {
  if (status === 'new') {
    return <span className="px-3 py-1 rounded-full bg-amber-400 text-white text-xs font-bold">New</span>;
  }
  if (status === 'pending') {
    return <span className="px-3 py-1 rounded-full bg-orange-500 text-white text-xs font-bold">Pending</span>;
  }
  return <span className="px-3 py-1 rounded-full bg-emerald-500 text-white text-xs font-bold">Done</span>;
};

const StatCard = ({ label, count, active, onClick, testid }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className={`text-left rounded-2xl border-2 px-6 py-5 h-28 transition-all ${
      active ? 'border-primary bg-white shadow-card-hover' : 'border-mute/30 bg-surface-100 hover:bg-white'
    }`}
  >
    <div className="text-base font-medium text-ink/70">{label}</div>
    <div className="font-display text-3xl font-bold text-ink mt-1 tracking-tight">{count}</div>
  </button>
);

const Tasks = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { refresh: refreshNotifications } = useNotifications();
  const canAssign = user?.role === 'super_admin' || !!user?.can_assign_tasks;

  const [inbox, setInbox] = useState([]);
  const [sent, setSent] = useState([]);
  const [stats, setStats] = useState({ pending: 0, completed: 0 });
  const [sentFilter, setSentFilter] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [openTask, setOpenTask] = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const calls = [axios.get(`${API}/tasks/inbox`)];
      if (canAssign) {
        calls.push(axios.get(`${API}/tasks/sent/stats`));
        calls.push(axios.get(`${API}/tasks/sent`, { params: { status: sentFilter } }));
      }
      const results = await Promise.all(calls);
      setInbox(results[0].data.tasks || []);
      if (canAssign) {
        setStats(results[1].data);
        setSent(results[2].data.tasks || []);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load tasks');
    } finally {
      setLoading(false);
      refreshNotifications();
    }
  }, [canAssign, sentFilter, refreshNotifications]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const downloadFile = (e, taskId, name) => {
    e.stopPropagation();
    fetch(`${API}/tasks/${taskId}/file`, { credentials: 'include' })
      .then((r) => r.ok ? r.blob() : Promise.reject(new Error('Download failed')))
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = name || 'file'; a.click();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      })
      .catch(() => toast.error('Could not download the file'));
  };

  const handleModalChange = async () => {
    setOpenTask(null);
    await loadAll();
  };

  const myId = user?.id;

  return (
    <div className="p-8" data-testid="tasks-page">
      {/* ===== Inbox section ===== */}
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="font-display text-4xl font-bold text-ink tracking-tight">Your Task</h1>
          <p className="text-ink/60 mt-1">Things assigned to you</p>
        </div>
      </div>

      <div className="bg-surface-100/60 rounded-3xl border border-mute/30 overflow-hidden mb-12">
        <div className="grid grid-cols-12 gap-4 px-8 py-4 border-b border-mute/40 text-ink/70 font-semibold text-sm">
          <div className="col-span-5">Task</div>
          <div className="col-span-2">Due</div>
          <div className="col-span-2">File</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-1"></div>
        </div>
        {loading ? (
          <div className="p-12 text-center text-ink/50">Loading…</div>
        ) : inbox.length === 0 ? (
          <div className="p-12 text-center text-ink/50">No tasks assigned to you. 🎉</div>
        ) : (
          inbox.map((t) => {
            // Latest feedback NOT authored by me → there's a reply to show
            const lastFb = (t.feedbacks || []).slice(-1)[0];
            const hasIncomingReply = lastFb && lastFb.by_user_id !== myId;
            return (
            <button
              key={t.id}
              data-testid={`inbox-row-${t.id}`}
              onClick={() => setOpenTask(t)}
              className="w-full grid grid-cols-12 gap-4 px-8 py-4 border-b border-mute/20 last:border-b-0 items-center bg-white/40 hover:bg-white transition-colors text-left"
            >
              <div className="col-span-5 min-w-0">
                <div className="font-medium text-ink truncate flex items-center gap-2">
                  {t.title}
                  {hasIncomingReply && (
                    <span
                      data-testid={`reply-badge-${t.id}`}
                      title={`Reply from ${lastFb.by_name}`}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] font-bold border border-primary/20"
                    >
                      💬 Reply
                    </span>
                  )}
                </div>
                <div className="text-xs text-ink/50 mt-0.5">from {t.assigned_by_name}</div>
              </div>
              <div className="col-span-2 text-sm text-ink/80">{fmtDate(t.due_date)}</div>
              <div className="col-span-2 text-sm text-ink/80 truncate">
                {t.file_name ? (
                  <span
                    role="button"
                    onClick={(e) => downloadFile(e, t.id, t.file_name)}
                    className="inline-flex items-center gap-1.5 text-primary hover:underline cursor-pointer"
                  >
                    <Download className="w-3.5 h-3.5" />
                    <span className="truncate max-w-[120px]">{t.file_name}</span>
                  </span>
                ) : '—'}
              </div>
              <div className="col-span-2"><StatusPill status={t.status} /></div>
              <div className="col-span-1 text-right text-xs text-primary font-semibold">View →</div>
            </button>
          );})
        )}
      </div>

      {/* ===== Assigner section ===== */}
      {canAssign && (
        <>
          <div className="flex items-end justify-between mb-6">
            <div>
              <h2 className="font-display text-3xl font-bold text-ink tracking-tight">Assigned by you</h2>
              <p className="text-ink/60 mt-1">Track everything you have delegated</p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4 max-w-3xl mb-8">
            <StatCard
              testid="stat-pending"
              label="Pending"
              count={stats.pending}
              active={sentFilter === 'pending'}
              onClick={() => setSentFilter('pending')}
            />
            <StatCard
              testid="stat-completed"
              label="Completed"
              count={stats.completed}
              active={sentFilter === 'completed'}
              onClick={() => setSentFilter('completed')}
            />
            <button
              data-testid="new-task-card"
              onClick={() => navigate('/tasks/new')}
              className="rounded-2xl bg-primary text-white shadow-card hover:shadow-card-hover hover:bg-primary-hover transition-all flex items-center justify-center h-28"
            >
              <Plus className="w-10 h-10" />
            </button>
          </div>

          <div className="bg-surface-100/60 rounded-3xl border border-mute/30 overflow-hidden">
            <div className="grid grid-cols-12 gap-4 px-8 py-4 border-b border-mute/40 text-ink/70 font-semibold text-sm">
              <div className="col-span-5">Task</div>
              <div className="col-span-2">Due</div>
              <div className="col-span-2">Assigned To</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-1"></div>
            </div>
            {sent.length === 0 ? (
              <div className="p-12 text-center text-ink/50">No {sentFilter} tasks.</div>
            ) : (
              sent.map((t) => {
                const lastFb = (t.feedbacks || []).slice(-1)[0];
                const hasIncomingReply = lastFb && lastFb.by_user_id !== myId;
                return (
                <button
                  key={t.id}
                  data-testid={`sent-row-${t.id}`}
                  onClick={() => setOpenTask(t)}
                  className="w-full grid grid-cols-12 gap-4 px-8 py-4 border-b border-mute/20 last:border-b-0 items-center bg-white/40 hover:bg-white transition-colors text-left"
                >
                  <div className="col-span-5 min-w-0">
                    <div className="font-medium text-ink truncate flex items-center gap-2">
                      {t.title}
                      {hasIncomingReply && (
                        <span
                          data-testid={`sent-reply-badge-${t.id}`}
                          title={`Reply from ${lastFb.by_name}`}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] font-bold border border-primary/20"
                        >
                          💬 Reply
                        </span>
                      )}
                    </div>
                    {t.description && <div className="text-xs text-ink/50 truncate mt-0.5">{t.description}</div>}
                  </div>
                  <div className="col-span-2 text-sm text-ink/80">{fmtDate(t.due_date)}</div>
                  <div className="col-span-2 text-sm text-ink/80">{t.assigned_to_name}</div>
                  <div className="col-span-2"><StatusPill status={t.status} /></div>
                  <div className="col-span-1 text-right text-xs text-primary font-semibold flex items-center justify-end gap-1">
                    {t.status !== 'done' && <Bell className="w-3.5 h-3.5" />}View →
                  </div>
                </button>
              );})
            )}
          </div>
        </>
      )}

      {openTask && (
        <TaskDetailModal
          task={openTask}
          canChange={openTask.assigned_to === myId || openTask.assigned_by === myId}
          canRenotify={openTask.assigned_by === myId}
          currentUserId={myId}
          onStatusChange={handleModalChange}
          onRenotify={handleModalChange}
          onClose={() => setOpenTask(null)}
        />
      )}
    </div>
  );
};

export default Tasks;
